import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import api, { ensureBackendReady } from './api';
import { withBackendWakeup } from './utils/backendWakeup';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const wakeBackend = useCallback(() => ensureBackendReady(), []);
  const requestWithWakeup = useCallback(
    (action) => withBackendWakeup(action, wakeBackend),
    [wakeBackend]
  );

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return undefined;
    }

    requestWithWakeup(() => api.get('/users/me'))
      .then(res => {
        if (!cancelled) setUser(res.data);
      })
      .catch(() => {
        if (!cancelled) localStorage.removeItem('token');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [requestWithWakeup]);

  const login = async (email, password) => {
    const res = await requestWithWakeup(() => api.post('/users/login', { email, password }));
    localStorage.setItem('token', res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const demoLogin = async () => {
    const res = await requestWithWakeup(() => api.post('/users/demo-login'));
    localStorage.setItem('token', res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const register = async (username, email, password) => {
    const res = await requestWithWakeup(() => api.post('/users/register', { username, email, password }));
    localStorage.setItem('token', res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  const updateUser = (nextUser) => {
    setUser(prev => ({ ...(prev || {}), ...(nextUser || {}) }));
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, demoLogin, register, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
